<template>
  <section class="cards">
    <article class="card">
      <div class="cardB">
        <div v-if="tabs && tabs.length" class="tabs-row">
          <el-tabs v-model="localTab" class="tabs" @tab-click="onTabClick">
            <el-tab-pane v-for="t in tabs" :key="t.value" :name="String(t.value)" :label="t.label" />
          </el-tabs>
          <slot name="tabsRight" />
        </div>

        <div v-if="showFilterBar" class="search-bar">
          <!-- 业务配置筛选项：filterSchema + filterValues -->
          <el-form
            v-if="usesFilterSchema"
            class="search-form"
            label-position="top"
            @submit.native.prevent
          >
            <div class="filter-grid filter-grid--schema">
              <el-form-item
                v-for="(item, idx) in normalizedFilterSchema"
                :key="item.field + '-' + idx"
                class="filter-schema-field"
                :label="item.label"
                style="margin-bottom: 0;"
              >
                <el-input
                  v-if="item.type === 'input'"
                  v-model="localFilterValues[item.field]"
                  :placeholder="item.placeholder"
                  clearable
                  size="small"
                  @keyup.enter.native="onSearch"
                />
                <el-select
                  v-else-if="item.type === 'select'"
                  v-model="localFilterValues[item.field]"
                  :placeholder="item.placeholder || '请选择'"
                  :style="item.selectStyle"
                  size="small"
                  @change="onSchemaFieldChange(item)"
                >
                  <el-option
                    v-for="(opt, oi) in item.options"
                    :key="item.field + '-opt-' + oi + '-' + String(opt.value)"
                    :label="opt.label"
                    :value="opt.value"
                  />
                </el-select>
              </el-form-item>
            </div>

            <div v-if="showSearchButton" class="filter-actions">
              <el-button size="small" type="primary" native-type="button" @click="onSearch">搜索</el-button>
              <el-button size="small" native-type="button" @click="onReset">重置</el-button>
            </div>
          </el-form>

          <!-- 兼容：仅关键字 -->
          <el-form
            v-else-if="enableKeyword"
            class="search-form"
            label-position="top"
            @submit.native.prevent
          >
            <div class="filter-grid filter-grid--legacy">
              <el-form-item class="filter-item" label="关键字" style="margin-bottom: 0;">
                <el-input
                  v-model="localKeyword"
                  :placeholder="keywordPlaceholder"
                  clearable
                  prefix-icon="el-icon-search"
                  @keyup.enter.native="onSearch"
                />
              </el-form-item>
              <slot name="searchRight" />
            </div>

            <div v-if="showSearchButton" class="filter-actions">
              <el-button size="small" type="primary" native-type="button" @click="onSearch">搜索</el-button>
              <el-button size="small" native-type="button" @click="onReset">重置</el-button>
            </div>
          </el-form>
        </div>

        <div class="table-container">
          <div v-loading="loading" class="table-loading-wrap">
            <el-table
              :data="displayRows"
              class="table"
              :height="tableHeightPx"
              :row-class-name="rowClassName"
              :cell-class-name="cellClassName"
              :header-cell-class-name="'table-header-cell'"
              :show-header="true"
              :empty-text="emptyText"
              :highlight-current-row="false"
            >
              <el-table-column
                v-for="col in normalizedColumns"
                :key="col.key"
                :label="col.label"
                :prop="col.prop"
                :width="col.width"
                :min-width="col.minWidth"
                :align="col.align"
                :fixed="col.fixed"
                :show-overflow-tooltip="col.showOverflowTooltip"
              >
                <template slot-scope="scope">
                  <template v-if="scope.row && scope.row.__isFiller">
                    <span class="filler-cell">&nbsp;</span>
                  </template>
                  <template v-else-if="col.slot">
                    <slot :name="`col-${col.slot}`" :row="scope.row" :column="col" :index="scope.$index" />
                  </template>
                  <template v-else>
                    {{ scope.row && col.prop ? scope.row[col.prop] : "" }}
                  </template>
                </template>
              </el-table-column>
            </el-table>
          </div>
  
          <div class="pagination">
            <el-pagination
              background
              :hide-on-single-page="false"
              :current-page="localPage"
              :page-size="localPageSize"
              :page-sizes="normalizedPageSizes"
              :total="paginationTotal"
              :pager-count="normalizedPagerCount"
              :layout="paginationLayoutValue"
              @size-change="onPageSizeChange"
              @current-change="setPage"
            />
          </div>

        </div>

      </div>
    </article>
  </section>
</template>

<script>
export default {
  name: "CommonFilterTableCard",
  props: {
    title: { type: String, required: true },
    items: { type: Array, default: () => [] },

    pageSize: { type: Number, default: 12 },
    page: { type: Number, default: 1 },
    tableVisibleRows: { type: Number, default: 10 },
    tableRowHeight: { type: Number, default: 44 },
    tableHeaderHeight: { type: Number, default: 48 },

    columnsCount: { type: Number, default: 1 }, // legacy, kept for compatibility
    keepHeight: { type: Boolean, default: true },

    emptyText: { type: String, default: "暂无数据" },

    tabs: { type: Array, default: () => [] }, // [{ value, label }]
    tab: { type: String, default: "all" },
    tabField: { type: String, default: "" },

    enableKeyword: { type: Boolean, default: false },
    keyword: { type: String, default: "" },
    keywordPlaceholder: { type: String, default: "请输入关键字" },
    keywordFields: { type: Array, default: () => [] }, // string field name OR (row)=>string
    showSearchButton: { type: Boolean, default: true },

    /**
     * 筛选项配置（与 enableKeyword 二选一优先使用本配置）
     * [{ label, field | filed, type: 'input' | 'select', options?, placeholder?, width?, immediate? }]
     * immediate：变更时是否立即触发 filter-change（默认 select 为 true，input 为 false）
     */
    filterSchema: { type: Array, default: () => [] },
    /** 与 filterSchema 字段对应的值（.sync） */
    filterValues: { type: Object, default: () => ({}) },

    // [{ key?, label, prop?, width?, minWidth?, align?, fixed?, showOverflowTooltip?, slot? }]
    columns: { type: Array, default: () => [] },

    // (items, { tab, keyword, filters? }) => filteredItems
    filterFn: { type: Function, default: null },

    // 后端分页：items 只包含当前页数据时开启
    useServerPagination: { type: Boolean, default: false },
    // 后端返回的总条数（用于分页 total/pages）
    total: { type: Number, default: 0 },

    // 列表加载态（只覆盖表格区域）
    loading: { type: Boolean, default: false },

    // 分页最大可见页码按钮数（Element 要求 5-21 且为奇数）
    pagerCount: { type: Number, default: 11 },
    // 分页可选每页条数
    pageSizes: { type: Array, default: () => [10, 20, 30, 40, 50] },
    // 是否显示每页条数选择器（sizes）
    showPageSizeSelector: { type: Boolean, default: true },
    // 是否显示跳页输入框（jumper）
    showPageJumper: { type: Boolean, default: true },
    // 自定义分页布局（优先级最高）
    paginationLayout: { type: String, default: "" },
  },
  data() {
    return {
      localPage: this.page || 1,
      localPageSize: this.pageSize || 12,
      localTab: this.tab || "all",
      localKeyword: this.keyword || "",
      localFilterValues: {},
    };
  },
  computed: {
    usesFilterSchema() {
      return Array.isArray(this.filterSchema) && this.filterSchema.length > 0;
    },
    showFilterBar() {
      return this.usesFilterSchema || this.enableKeyword;
    },
    normalizedFilterSchema() {
      const list = Array.isArray(this.filterSchema) ? this.filterSchema : [];
      return list.map((raw, idx) => {
        const field = raw && (raw.field != null ? raw.field : raw.filed);
        const type = raw && raw.type === "select" ? "select" : "input";
        const immediate =
          raw && raw.immediate != null ? raw.immediate : false;
        const width = raw && raw.width != null ? raw.width : undefined;
        return {
          label: (raw && raw.label) || "",
          field: String(field || `field_${idx}`),
          type,
          options: type === "select" && Array.isArray(raw && raw.options) ? raw.options : [],
          placeholder: (raw && raw.placeholder) || "",
          prefixIcon: raw && raw.prefixIcon != null ? raw.prefixIcon : "",
          immediate,
          selectStyle:
            width != null
              ? { width: typeof width === "number" ? `${width}px` : width }
              : {},
        };
      });
    },
    normalizedColumns() {
      const cols = Array.isArray(this.columns) ? this.columns : [];
      return cols.map((c, idx) => ({
        key: c && (c.key || c.prop || c.slot || String(idx)),
        label: (c && c.label) || "",
        prop: c && c.prop ? String(c.prop) : "",
        width: c && c.width != null ? c.width : undefined,
        minWidth: c && c.minWidth != null ? c.minWidth : undefined,
        align: (c && c.align) || "left",
        fixed: c && c.fixed != null ? c.fixed : undefined,
        showOverflowTooltip: c && c.showOverflowTooltip != null ? c.showOverflowTooltip : true,
        slot: c && c.slot ? String(c.slot) : "",
      }));
    },
    filterCtx() {
      const ctx = { tab: this.localTab, keyword: this.localKeyword };
      if (this.usesFilterSchema) {
        ctx.filters = { ...this.localFilterValues };
      }
      return ctx;
    },
    filteredItems() {
      const items = Array.isArray(this.items) ? this.items : [];
      // 后端分页时，不再做前端过滤/切片；交由接口返回的数据决定展示内容
      if (this.useServerPagination) return items;

      const ctx = this.filterCtx;

      if (typeof this.filterFn === "function") {
        const out = this.filterFn(items, ctx);
        return Array.isArray(out) ? out : [];
      }

      let list = items;
      if (this.localTab !== "all" && this.tabField) {
        list = list.filter((row) => String(row && row[this.tabField]) === String(this.localTab));
      }

      const kw = (this.localKeyword || "").trim().toLowerCase();
      if (kw && this.keywordFields && this.keywordFields.length) {
        list = list.filter((row) =>
          this.keywordFields.some((f) => {
            try {
              const v = typeof f === "function" ? f(row) : row && row[f];
              return String(v == null ? "" : v).toLowerCase().includes(kw);
            } catch (e) {
              return false;
            }
          })
        );
      }

      return list;
    },
    paginationTotal() {
      return this.useServerPagination ? this.total || 0 : this.filteredItems.length;
    },
    normalizedPagerCount() {
      const raw = Number(this.pagerCount);
      if (!Number.isFinite(raw)) return 11;
      let n = Math.floor(raw);
      if (n < 5) n = 5;
      if (n > 21) n = 21;
      if (n % 2 === 0) n -= 1;
      return n;
    },
    normalizedPageSizes() {
      const arr = Array.isArray(this.pageSizes) ? this.pageSizes : [];
      const normalized = arr
        .map((n) => parseInt(n, 10))
        .filter((n) => Number.isFinite(n) && n > 0);
      const set = new Set(normalized.length ? normalized : [10, 20, 30, 40, 50]);
      set.add(parseInt(this.localPageSize, 10) || 10);
      return Array.from(set).sort((a, b) => a - b);
    },
    normalizedTableVisibleRows() {
      const n = parseInt(this.tableVisibleRows, 10);
      return Number.isFinite(n) && n > 0 ? n : 10;
    },
    tableHeightPx() {
      const rowH = parseInt(this.tableRowHeight, 10);
      const headerH = parseInt(this.tableHeaderHeight, 10);
      const validRowH = Number.isFinite(rowH) && rowH > 0 ? rowH : 44;
      const validHeaderH = Number.isFinite(headerH) && headerH > 0 ? headerH : 48;
      return validHeaderH + this.normalizedTableVisibleRows * validRowH;
    },
    paginationLayoutValue() {
      const custom = String(this.paginationLayout || "").trim();
      if (custom) return custom;
      const parts = ["total"];
      if (this.showPageSizeSelector) parts.push("sizes");
      parts.push("prev", "pager", "next");
      if (this.showPageJumper) parts.push("jumper");
      return parts.join(", ");
    },
    pages() {
      if (this.useServerPagination) {
        return Math.ceil((this.paginationTotal || 0) / this.localPageSize) || 1;
      }
      return Math.ceil(this.filteredItems.length / this.localPageSize) || 1;
    },
    pageItems() {
      if (this.useServerPagination) return this.filteredItems;

      const start = (this.localPage - 1) * this.localPageSize;
      return this.filteredItems.slice(start, start + this.localPageSize);
    },
    fillerRows() {
      if (this.useServerPagination) return [];
      if (!this.keepHeight) return [];
      const count = this.normalizedTableVisibleRows - this.pageItems.length;
      if (count <= 0) return [];
      return Array.from({ length: count }, (_, i) => ({
        __isFiller: true,
        __fillerKey: `${this.localPage}_${i}`,
      }));
    },
    displayRows() {
      return this.pageItems.concat(this.fillerRows);
    },
  },
  watch: {
    page(val) {
      const next = parseInt(val, 10) || 1;
      if (next !== this.localPage) this.localPage = next;
    },
    pageSize(val) {
      const next = parseInt(val, 10) || 10;
      if (next !== this.localPageSize) this.localPageSize = next;
    },
    tab(val) {
      const next = val == null ? "all" : String(val);
      if (next !== this.localTab) this.localTab = next;
    },
    keyword(val) {
      const next = val == null ? "" : String(val);
      if (next !== this.localKeyword) this.localKeyword = next;
    },
    normalizedFilterSchema: {
      immediate: true,
      handler() {
        this.syncLocalFilterValues();
      },
    },
    filterValues: {
      deep: true,
      handler() {
        this.syncLocalFilterValues();
      },
    },
    localTab(val, oldVal) {
      if (String(val) === String(oldVal)) return;
      // v-model 已先把 localTab 设为新值，不能再走 setTab（会与 next===localTab 早退冲突）
      this.$emit("update:tab", val);
      if (this.localPage !== 1) {
        this.localPage = 1;
        this.$emit("update:page", 1);
      }
      this.emitFilterChange();
    },
    pages(val) {
      if (this.localPage > val) this.setPage(Math.max(1, val));
      if (this.localPage < 1) this.setPage(1);
    },
  },
  methods: {
    defaultInputPrefix(item, idx) {
      if (idx === 0 && item.field === "search") return "el-icon-search";
      return "";
    },
    syncLocalFilterValues() {
      const base = {};
      this.normalizedFilterSchema.forEach((s) => {
        base[s.field] = "";
      });
      const parent = this.filterValues && typeof this.filterValues === "object" ? this.filterValues : {};
      this.localFilterValues = { ...base, ...parent };
    },
    emitFilterChange() {
      if (this.usesFilterSchema) {
        this.$emit("filter-change", {
          tab: this.localTab,
          keyword: this.localKeyword,
          filters: { ...this.localFilterValues },
        });
      } else {
        this.$emit("filter-change", { tab: this.localTab, keyword: this.localKeyword });
      }
    },
    onTabClick() {
      // v-model already updated; setTab is triggered by watcher
    },
    setPage(p) {
      const next = Math.min(this.pages, Math.max(1, parseInt(p, 10) || 1));
      if (next === this.localPage) return;
      this.localPage = next;
      this.$emit("update:page", next);
      this.$emit("page-change", next);
    },
    onPageSizeChange(size) {
      const nextSize = parseInt(size, 10) || 10;
      if (nextSize === this.localPageSize) return;
      this.localPageSize = nextSize;
      this.$emit("update:pageSize", nextSize);
      this.$emit("page-size-change", nextSize);
      if (this.localPage !== 1) {
        this.localPage = 1;
        this.$emit("update:page", 1);
      }
      this.$emit("page-change", 1);
    },
    onSearch() {
      if (this.usesFilterSchema) {
        this.$emit("update:filterValues", { ...this.localFilterValues });
        this.setPage(1);
        this.emitFilterChange();
        return;
      }
      this.$emit("update:keyword", this.localKeyword);
      this.setPage(1);
      this.emitFilterChange();
    },
    onReset() {
      if (this.usesFilterSchema) {
        const base = {};
        this.normalizedFilterSchema.forEach((s) => {
          base[s.field] = "";
        });
        this.localFilterValues = { ...base };
        this.$emit("update:filterValues", { ...this.localFilterValues });
        this.setPage(1);
        this.emitFilterChange();
        return;
      }
      this.localKeyword = "";
      this.$emit("update:keyword", this.localKeyword);
      this.setPage(1);
      this.emitFilterChange();
    },
    onSchemaFieldChange(item) {
      if (item && item.immediate) {
        this.onSearch();
      }
    },
    rowClassName({ row }) {
      if (row && row.__isFiller) return "is-filler-row";
      return "";
    },
    cellClassName({ row }) {
      if (row && row.__isFiller) return "is-filler-cell";
      return "";
    },
  },
};
</script>

<style scoped>
.table {
  width: 100%;
}

.table-container {
  border: 1px solid #eee;
}

.table-loading-wrap {
  width: 100%;
  background: #ffffff;
  padding: 16px;
}

.left,
.right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.left {
  min-width: 0;
}

.tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}

.tabs {
  flex: 1 1 auto;
  min-width: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
}



.search-form {
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.search-item {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0;
}

.search-form .el-form-item {
  margin-bottom: 0;
}

.filter-grid {
  display: grid;
  gap: 12px;
  width: 100%;
  min-width: 0;
  align-items: end;
}

.filter-grid--legacy {
  grid-template-columns: minmax(0, 1fr) auto auto;
}

/* 配置化筛选：一行三列，每项约占 1/3，多于 3 个自动换行 */
.filter-grid--schema {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.filter-grid--schema .filter-schema-field ::v-deep .el-form-item__content {
  width: 100%;
  min-width: 0;
}

.filter-grid--schema .filter-schema-field ::v-deep .el-input,
.filter-grid--schema .filter-schema-field ::v-deep .el-select {
  width: 100%;
}

.filter-grid .filter-item ::v-deep .el-form-item__content {
  width: 100%;
}

.filter-grid .filter-item ::v-deep .el-input {
  width: 100%;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  width: 100%;
  box-sizing: border-box;
}

.filter-grid ::v-deep .el-form-item,
.filter-schema-field ::v-deep .el-form-item {
  margin-bottom: 0;
}

.filter-grid ::v-deep .el-form-item__label,
.filter-schema-field ::v-deep .el-form-item__label {
  font-size: 14px;
  color: var(--muted);
  padding: 0;
  height: 30px;
  margin-bottom: 3px;
}

.pagination {
  background: #ffffff;
  padding: 16px;
  padding-top: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.filler-cell {
  display: inline-block;
  width: 1px;
  height: 1px;
}
</style>

<style>
/* Element-UI overrides scoped to this component root */
.cardB .el-table {
  font-size: 14px;
}

.cardB .el-table th.el-table__cell {
  background: #f8fafc;
  color: var(--muted);
  font-weight: 700;
}

.cardB .el-table td.el-table__cell,
.cardB .el-table th.el-table__cell {
  padding: 6px 0;
}

.cardB .el-table .cell {
  white-space: nowrap;
}

.cardB .el-table .is-filler-row td {
  background: #ffffff;
}

.cardB .el-table .is-filler-row:hover > td {
  background: #ffffff !important;
}

.cardB .el-table .is-filler-cell .cell {
  visibility: hidden;
}

.cardB .el-table__empty-text {
  text-decoration: none !important;
}

/* remove the extra bottom line when table is empty */
.cardB .el-table::before {
  height: 0;
}

/* also remove fixed-column bottom pseudo-line (right-bottom short line) */
.cardB .el-table__fixed::before,
.cardB .el-table__fixed-right::before {
  height: 0;
}

.cardB .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: var(--brand);
}

.cardB .el-pagination.is-background .btn-prev,
.cardB .el-pagination.is-background .btn-next,
.cardB .el-pagination.is-background .el-pager li {
  border-radius: 8px;
}

.cardB .el-tabs__header {
  margin: 0;
}

.cardB .el-tabs__nav-wrap::after {
  height: 0;
}

.cardB .el-tabs__item {
  font-size: 13px;
  padding: 0 14px;
  color: var(--muted);
}

.cardB .el-tabs__item.is-active {
  color: var(--brand);
}

.cardB .el-tabs__active-bar {
  background-color: var(--brand);
}

.cardB .el-button--primary {
  background-color: var(--brand);
  border-color: var(--brand);
}

/* 表格操作按钮统一为纯文字样式（仅作用于表格内容区） */
.cardB .el-table .el-button {
  background: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  min-height: auto;
  font-size: 14px;
  font-weight: 400 !important;
  font-family: inherit;
  color: var(--brand) !important;
  line-height: 1.4;
}

.cardB .el-table .el-button--text,
.cardB .el-table .el-button--primary,
.cardB .el-table .el-button--danger {
  color: var(--brand) !important;
}

.cardB .el-table .el-button + .el-button {
  margin-left: 0 !important;
}
</style>
